// .ts extension: these modules are in the node --test import graph, which resolves literally
import { readJwtClaims } from '../api/jwt.ts';

/**
 * Whether the IZTeach session in hand can use the school's bank, decided before any request.
 *
 * The bank sits behind the exam gateway's authorizer: GET /question/admin, POST, PUT and DELETE
 * are allowed for roles admin and teacher only (examination-service src/policies.js). The token
 * comes from the teacher sign-in at /setup; the QLT exchange mints a student token, which those
 * routes refuse.
 */

/** Roles examination-service policies.js lets read the full question and write it. */
const BANK_ROLES = ['teacher', 'admin'];

export type BankAccess =
  | { status: 'ok'; userId: string | null }
  | { status: 'signed-out' }
  | { status: 'not-teacher' };

/**
 * `fallbackUserId` is the id the sign-in response gave, used only when the token cannot be read.
 * An unreadable token fails OPEN: the server's answer is more legible than a guess.
 */
export function bankAccess(accessToken: string | null, fallbackUserId: string | null): BankAccess {
  if (!accessToken) return { status: 'signed-out' };
  const claims = readJwtClaims(accessToken);
  if (!claims) return { status: 'ok', userId: fallbackUserId };
  const roles = Array.isArray(claims.roles) ? claims.roles.map(String) : null;
  if (roles && !roles.some((role) => BANK_ROLES.indexOf(role) >= 0)) return { status: 'not-teacher' };
  // createdBy is stamped from this very claim (question/controller.js create), so "mine" asks by it
  const userId = typeof claims.userId === 'string' && claims.userId ? claims.userId : fallbackUserId;
  return { status: 'ok', userId };
}
